import { useContext, useEffect, useRef, useState } from 'react';
import SharedContext from '../../context';
import raiseError from '../../utils/raiseError';
import { buildContextEnrichment } from './utils/contextEnrichment';

/**
 * A hook used for wrapping gig listing state management.
 *
 * In case gigs are provided, the gigs are transformed and returned.
 *
 * In case fetchGigs are provided, an async call will be fired where
 * `isLoading` state is updated according to the promise resolve.
 *
 * In case of an error in fetchGigs, the isLoading would be set back to `false`.
 *
 * In addition the hook adds `contextEnrichment` parameters to be passed to the gig
 *
 * listing component.
 */
const useGigListing = ({ fetchGigs, pageContext, sourceComponent, collections = [], onUpdateCollections }) => {
    const { currency, sourceInfo = {} } = useContext(SharedContext);
    const [loading, setLoading] = useState(false);

    const { isTouch } = sourceInfo;

    const gigsRef = useRef([]);
    const contextEnrichmentRef = useRef(
        buildContextEnrichment({
            currency,
            pageContext,
            sourceComponent,
            isTouch,
        })
    );

    useEffect(() => {
        const handleGigsFetch = async () => {
            try {
                setLoading(true);

                gigsRef.current = await fetchGigs();
            } catch (e) {
                gigsRef.current = [];
                raiseError();
            }

            setLoading(false);
        };

        fetchGigs && handleGigsFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        gigs: gigsRef.current,
        contextEnrichment: contextEnrichmentRef.current,
        loading,
        collectProps: {
            collections,
            onUpdateCollections,
            sourceInfo,
        },
    };
};

export default useGigListing;
